import React from 'react';
import { useTranslation } from 'react-i18next';

function SortControls({ sortField, sortDirection, onSort, type }) {
  const { t } = useTranslation('dashboard');

  const bttsFields = [
    { key: 'composite_grade', label: t('sortGrade') },
    { key: 'confidence', label: t('sortConfidence') },
    { key: 'odds_btts', label: t('sortOdds') },
    { key: 'edge', label: t('sortEdge') },
    { key: 'total_gpg', label: t('sortTotalGPG') },
    { key: 'home_btts_pct', label: t('sortHomeBTTS') },
    { key: 'away_btts_pct', label: t('sortAwayBTTS') },
    { key: 'h2h_btts_pct', label: t('sortH2HBTTS') },
  ];

  const outcomeFields = [
    { key: 'composite_grade', label: t('sortGrade') },
    { key: 'confidence', label: t('sortConfidence') },
    { key: 'odds', label: t('sortOdds') },
    { key: 'value_edge', label: t('sortValueEdge') },
    { key: 'prob_home', label: t('sortHome') },
    { key: 'prob_draw', label: t('sortDraw') },
    { key: 'prob_away', label: t('sortAway') },
    { key: 'ppg_diff', label: t('sortPPGDiff') },
  ];

  const drawFields = [
    { key: 'composite_grade', label: t('sortGrade') },
    { key: 'confidence', label: t('sortConfidence') },
    { key: 'odds_draw', label: t('sortDrawOdds') },
    { key: 'draw_prob', label: t('sortDrawProb') },
    { key: 'value_edge', label: t('sortValueEdge') },
    { key: 'draw_tendency', label: t('sortDrawTendency') },
    { key: 'odds_diff', label: t('sortOddsDiff') },
    { key: 'ppg_diff', label: t('sortPPGDiff') },
    { key: 'combined_gpg', label: t('sortCombinedGPG') },
  ];

  const correctScoreFields = [
    { key: 'composite_grade', label: t('sortGrade') },
    { key: 'score_probability', label: t('sortScoreProb') },
    { key: 'estimated_fair_odds', label: t('sortFairOdds') },
    { key: 'btts_avg', label: t('sortBTTSAvg') },
    { key: 'avg_goals', label: t('sortAvgGoals') },
    { key: 'over25_avg', label: t('sortOver25') },
    { key: 'under15_avg', label: t('sortUnder15') },
    { key: 'combined_gpg', label: t('sortCombinedGPG') },
    { key: 'expected_home_goals', label: t('sortExpHome') },
    { key: 'expected_away_goals', label: t('sortExpAway') },
  ];

  const ultimateFields = [
    { key: 'composite_grade', label: t('sortGrade') },
    { key: 'confidence', label: t('sortConfidence') },
    { key: 'expected_hit_rate', label: t('sortHitRate') },
    { key: 'model_count', label: t('sortModels') },
    { key: 'quality_signals.draw_tendency', label: t('sortDrawTendency') },
    { key: 'quality_signals.ppg_diff', label: t('sortPPGDiff') },
    { key: 'quality_signals.odds_diff', label: t('sortOddsDiff') },
    { key: 'quality_signals.red_flag_count', label: t('sortRedFlags') },
    { key: 'quality_signals.btts_avg', label: t('sortBTTSAvg') },
    { key: 'combined_gpg', label: t('sortCombinedGPG') },
    { key: 'home_xg', label: t('sortHomeXG') },
    { key: 'away_xg', label: t('sortAwayXG') },
  ];

  const over25Fields = [
    { key: 'composite_grade', label: t('sortGrade') },
    { key: 'confidence', label: t('sortConfidence') },
    { key: 'odds_over25', label: t('sortOdds') },
    { key: 'edge', label: t('sortEdge') },
    { key: 'total_gpg', label: t('sortTotalGPG') },
    { key: 'home_gpg', label: t('sortHomeGPG') },
    { key: 'away_gpg', label: t('sortAwayGPG') },
    { key: 'over25_pct', label: t('sortOver25Pct') },
    { key: 'avg_goals', label: t('sortAvgGoals') },
  ];

  const htftFields = [
    { key: 'composite_grade', label: t('sortGrade') },
    { key: 'htft_prob', label: t('sortHtftProb') },
    { key: 'odds', label: t('sortOdds') },
    { key: 'value_edge', label: t('sortValueEdge') },
    { key: 'ppg_diff', label: t('sortPPGDiff') },
    { key: 'combined_gpg', label: t('sortCombinedGPG') },
    { key: 'home_ht_gpg', label: t('sortHomeHtGPG') },
    { key: 'away_ht_gpg', label: t('sortAwayHtGPG') },
  ];

  const aiPicksFields = [
    { key: 'conviction_score', label: t('sortConviction') },
    { key: 'composite_grade', label: t('sortGrade') },
    { key: 'consensus_level', label: t('sortConsensus') },
    { key: 'odds', label: t('sortOdds') },
  ];

  const favoritesFields = [
    { key: 'composite_grade', label: t('sortGrade') },
    { key: 'date_time', label: t('sortDate') },
  ];

  const fields = type === 'favorites' ? favoritesFields : type === 'btts' ? bttsFields : type === 'draw' ? drawFields : type === 'correct_score' ? correctScoreFields : type === 'over25' ? over25Fields : type === 'htft' ? htftFields : type === 'ultimate' ? ultimateFields : type === 'ai_picks' ? aiPicksFields : outcomeFields;
  const currentField = fields.find(f => f.key === sortField);

  const handleChange = (e) => {
    const newField = e.target.value;
    onSort(newField);
  };

  const toggleDirection = () => {
    // Toggle direction by calling onSort with current field
    onSort(sortField);
  };

  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-gray-500">{t('sortBy')}</span>
      <div className="relative">
        <select
          value={sortField}
          onChange={handleChange}
          className="appearance-none bg-anthracite-800 border border-anthracite-700 rounded-lg px-3 py-2 pr-8 text-sm text-gray-300 focus:outline-none focus:border-accent/50 hover:border-anthracite-600 cursor-pointer"
        >
          {fields.map(field => (
            <option key={field.key} value={field.key}>
              {field.label}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      <button
        onClick={toggleDirection}
        className="flex items-center gap-1 px-3 py-2 bg-anthracite-800 border border-anthracite-700 rounded-lg text-sm text-gray-300 hover:border-accent/50 hover:text-accent transition-colors"
        title={sortDirection === 'desc' ? 'Highest first' : 'Lowest first'}
      >
        {sortDirection === 'desc' ? (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <span className="text-xs">{t('high')}</span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
            <span className="text-xs">{t('low')}</span>
          </>
        )}
      </button>
    </div>
  );
}

export default SortControls;
